import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { TRANSLATIONS } from '../constants/translations';

/**
 * The phases a rental can go through, in order. NOT every booking has every
 * one of them -- see buildBookingTimeline, which drops the ones that don't
 * apply to a given booking's own shape (a self-pickup dry hire has no
 * transport or collection phase at all, and an immediate-pay booking has no
 * quote phase). Deliberately its own vocabulary rather than a re-use of
 * BookingStatus: status answers "what state is this booking's contract in"
 * (Confirmed/Cancelled/...), these answer "where is the machine right now",
 * which is what the guest and host actually want to see.
 */
export type BookingPhaseKey =
  | 'quote'
  // Two faces of the same step. A booking that hasn't been paid yet is NOT
  // confirmed, and must not borrow the confirmed step's tick or its "payment
  // went through" line -- see buildBookingTimeline's own comment.
  | 'awaitingPayment'
  | 'confirmed'
  | 'transport'
  | 'handover'
  | 'inUse'
  | 'offRent'
  | 'collection'
  | 'return'
  | 'settlement';

export type BookingPhaseState = 'done' | 'current' | 'upcoming' | 'cancelled';

/**
 * Who is actually holding the guest's rental money at a given phase.
 *
 * 'guest' -- not charged yet (or only pre-authorized, never captured).
 * 'platform' -- charged, but held by the marketplace, not yet transferred.
 * 'host' -- transferred to the host's own Stripe connected account.
 * 'refunded' -- returned to the guest.
 *
 * Every booking is charged platform-held now, so a freshly paid one reports
 * 'platform' and stays there until the host has earned it -- for a delivered
 * machine, a day after the delivery note is signed (see the backend's
 * BookingFundsReleaseService). Bookings created before that change were
 * straight-through destination charges and correctly report 'host' from the
 * moment they were paid. The UI hardcodes neither story: it renders
 * whichever one the data describes, so it can't end up claiming the money is
 * somewhere it isn't.
 */
export type MoneyLocation = 'guest' | 'held' | 'platform' | 'host' | 'refunded';

export interface IBookingPhase {
  key: BookingPhaseKey;
  state: BookingPhaseState;
  /** Translation key for the phase's own name. */
  labelKey: string;
  /** Translation key for the one-line explanation shown under the name while this phase is the current one. */
  descriptionKey: string;
  /** When this phase actually happened -- undefined for anything not reached yet. */
  at?: string;
  /** Where the rental money sits once this phase is reached. */
  money: MoneyLocation;
}

/**
 * Whether a booking has reached the point where its money was actually taken.
 *
 * 'PartiallyRefunded' counts, and has to: that booking gave some of the
 * money back and kept the rest, and it is still running, still due its
 * handover, still due its payout. Reading it as unpaid would have the
 * timeline walk backwards -- a rental halfway through would suddenly show
 * its money sitting with the guest and every phase after payment as not yet
 * reached. Only a FULL refund ends a booking's money story, which is what
 * the 'Refunded' checks below are for.
 */
const isPaid = (booking: IBooking) =>
  booking.paymentStatus === 'Succeeded' || booking.paymentStatus === 'PartiallyRefunded';

/**
 * Money committed to this booking but not yet taken -- a charge-at-handover
 * booking whose funds are frozen on the guest's card (see
 * IBooking.captureMode).
 *
 * Deliberately NOT folded into isPaid. The money is still the guest's, so
 * nothing downstream may treat it as collected; but it is not "with the
 * guest" in the ordinary sense either, because they cannot spend it. It is a
 * third place, and the timeline says so rather than picking whichever of the
 * two existing answers is less wrong.
 */
const isAuthorized = (booking: IBooking) => booking.paymentStatus === 'Authorized';

/**
 * Whether this booking's deposit has stopped being able to change -- either
 * it was resolved (released, or captured against damage) or there was never
 * one to resolve. A booking whose hold is still 'Authorized' has one last
 * open question about its money, however finished the rental itself looks.
 */
const depositSettled = (booking: IBooking) =>
  !booking.depositAmount || (booking.depositStatus !== undefined && booking.depositStatus !== 'Authorized');

/**
 * Where the rental money sits RIGHT NOW -- see MoneyLocation's own doc
 * comment for why this is read off the data rather than assumed.
 */
export const currentMoneyLocation = (booking: IBooking): MoneyLocation => {
  if (booking.paymentStatus === 'Refunded') return 'refunded';
  // Frozen on the guest's card: still theirs, and not spendable. Saying
  // 'guest' here would be true in law and misleading in practice -- the
  // customer who sees that on their statement wants to know why they cannot
  // touch it, and "we have not charged you" does not answer that.
  if (isAuthorized(booking)) return 'held';
  if (!isPaid(booking)) return 'guest';
  // The platform holds every booking's money from the moment the card
  // clears until the host has earned it (see the backend's own
  // BookingFundsReleaseService), so this is the normal state of a freshly
  // paid booking. It is still read defensively rather than assumed: a
  // booking created before escrow existed was a straight-through charge to
  // the host and correctly reports neither flag.
  if (booking.fundsHeldByPlatform && !booking.fundsReleasedToHostAt) return 'platform';
  return 'host';
};

/**
 * Every phase this specific booking goes through, in order, each already
 * resolved to done/current/upcoming -- the single source the timeline UI
 * renders from, so the component itself holds no lifecycle rules.
 *
 * The phase list is DERIVED FROM THE BOOKING'S OWN SHAPE, never a fixed
 * list: a guest who collects the machine themselves never sees a transport
 * phase, one who returns it themselves never sees a collection phase, and an
 * immediate-pay booking never sees the quote phase. Showing a permanently
 * greyed-out step for something that will never happen reads as "stuck"
 * rather than "not applicable".
 */
export const buildBookingTimeline = (booking: IBooking): IBookingPhase[] => {
  const cancelled = booking.status === 'Cancelled' || booking.status === 'HostNoShow' || booking.status === 'GuestNoShow';
  const paid = isPaid(booking);
  const moneyNow = currentMoneyLocation(booking);

  // Where the money sits once the rental is actually running -- see
  // MoneyLocation. Held bookings release at the handover (signed delivery
  // note), so anything from that phase onward is the host's.
  const moneyAfterHandover: MoneyLocation = booking.paymentStatus === 'Refunded' ? 'refunded' : 'host';

  const phases: IBookingPhase[] = [];

  // Only ever present for a reserva diferida -- an immediate-pay booking was
  // never quoted, it was just paid.
  if (booking.quoteHistory && booking.quoteHistory.length > 0) {
    phases.push({
      key: 'quote',
      state: booking.status === 'AwaitingQuote' ? 'current' : 'done',
      labelKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_QUOTE,
      descriptionKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_QUOTE_DESCRIPTION,
      at: booking.quoteHistory[0]?.createdAt,
      money: 'guest',
    });
  }

  // Until the money is actually taken this step is "pending payment", not
  // "confirmed". It used to keep the confirmed label, the confirmed tick and
  // the line "payment went through and the machine is reserved for your
  // dates" while merely being the CURRENT step -- so an accepted quote that
  // nobody had paid yet told the guest their booking was confirmed and paid.
  // Both are false at that point, and the second one is the kind of false
  // that stops someone paying.
  phases.push(
    paid
      ? {
          key: 'confirmed',
          state: 'done',
          labelKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_CONFIRMED,
          descriptionKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_CONFIRMED_DESCRIPTION,
          at: booking.createdAt,
          money: moneyNow,
        }
      : {
          key: 'awaitingPayment',
          state: 'current',
          labelKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_AWAITING_PAYMENT,
          descriptionKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_AWAITING_PAYMENT_DESCRIPTION,
          at: undefined,
          money: 'guest',
        }
  );

  if (booking.deliveryRequested) {
    phases.push({
      key: 'transport',
      state: booking.transportStartedAt ? 'done' : 'upcoming',
      labelKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_TRANSPORT,
      descriptionKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_TRANSPORT_DESCRIPTION,
      at: booking.transportStartedAt,
      money: moneyNow,
    });
  }

  phases.push({
    key: 'handover',
    state: booking.handedOverAt ? 'done' : 'upcoming',
    // Collecting it yourself and having it delivered are the same moment in
    // contract terms (the machine changes hands and the delivery note is
    // signed), but they read very differently to a guest, so they get their
    // own wording.
    labelKey: booking.deliveryRequested ? TRANSLATIONS.BOOKING_TIMELINE_PHASE_HANDOVER_DELIVERY : TRANSLATIONS.BOOKING_TIMELINE_PHASE_HANDOVER_PICKUP,
    descriptionKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_HANDOVER_DESCRIPTION,
    at: booking.handedOverAt,
    money: booking.handedOverAt ? moneyAfterHandover : moneyNow,
  });

  phases.push({
    key: 'inUse',
    state: booking.handedOverAt && !booking.offRentAt ? 'current' : booking.offRentAt ? 'done' : 'upcoming',
    labelKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_IN_USE,
    descriptionKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_IN_USE_DESCRIPTION,
    at: booking.handedOverAt,
    money: moneyAfterHandover,
  });

  phases.push({
    key: 'offRent',
    state: booking.offRentAt ? 'done' : 'upcoming',
    labelKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_OFF_RENT,
    descriptionKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_OFF_RENT_DESCRIPTION,
    at: booking.offRentAt,
    money: moneyAfterHandover,
  });

  if (booking.returnPickupRequested) {
    phases.push({
      key: 'collection',
      state: booking.collectionStartedAt ? 'done' : 'upcoming',
      labelKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_COLLECTION,
      descriptionKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_COLLECTION_DESCRIPTION,
      at: booking.collectionStartedAt,
      money: moneyAfterHandover,
    });
  }

  phases.push({
    key: 'return',
    state: booking.returnedAt ? 'done' : 'upcoming',
    labelKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_RETURN,
    descriptionKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_RETURN_DESCRIPTION,
    at: booking.returnedAt,
    money: moneyAfterHandover,
  });

  phases.push({
    key: 'settlement',
    // Completed is not the end of the money: the deposit is still held for
    // a day or two afterwards while any damage claim is made, and that hold
    // is the last thing on this booking that can still change. So this phase
    // only closes once the deposit has actually been resolved -- or
    // immediately, for a booking that never had one to resolve.
    state: booking.status === 'Completed' && depositSettled(booking) ? 'done' : 'upcoming',
    labelKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_SETTLEMENT,
    descriptionKey: TRANSLATIONS.BOOKING_TIMELINE_PHASE_SETTLEMENT_DESCRIPTION,
    at: booking.depositReleasedAt ?? (booking.status === 'Completed' ? booking.endAt : undefined),
    money: moneyAfterHandover,
  });

  // A cancelled booking freezes wherever it got to: everything already done
  // stays done, and everything ahead of it is marked cancelled rather than
  // left looking like it's still coming.
  if (cancelled) {
    return phases.map((phase) => (phase.state === 'done' ? phase : { ...phase, state: 'cancelled' as const }));
  }

  // Exactly one phase can be the current one. Anything explicitly marked
  // current above wins; otherwise it's the first one not yet done, so the
  // timeline always has a "you are here" marker even before the new
  // milestone timestamps start arriving from the backend.
  if (!phases.some((phase) => phase.state === 'current')) {
    const firstPending = phases.find((phase) => phase.state === 'upcoming');
    if (firstPending) firstPending.state = 'current';
  }

  return phases;
};
